import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import * as api from "@/lib/api";
import type { Insert, Row, Update } from "@/types/database";

type Table = Parameters<typeof api.list>[0];

/** Fetch a list with a raw PostgREST query string. */
export function useList<T extends Table>(
  table: T,
  query = "",
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [table, "list", query],
    queryFn: () => api.list(table, query),
    enabled: options?.enabled ?? true,
  });
}

/** Fetch a single row by id with optional embed query. */
export function useOne<T extends Table>(table: T, id: number | null, query = "") {
  return useQuery({
    queryKey: [table, "one", id, query],
    queryFn: () => api.getOne(table, id as number, query),
    enabled: id != null,
  });
}

function useInvalidate(keys: QueryKey[]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
}

export function useCreate<T extends Table>(table: T, invalidates: QueryKey[] = [[table]]) {
  const invalidate = useInvalidate(invalidates);
  return useMutation({
    mutationFn: (body: Insert<T>) => api.create(table, body),
    onSuccess: invalidate,
  });
}

export function useUpdate<T extends Table>(table: T, invalidates: QueryKey[] = [[table]]) {
  const invalidate = useInvalidate(invalidates);
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Update<T> }) =>
      api.update(table, id, body),
    onSuccess: invalidate,
  });
}

export function useUpsert<T extends Table>(
  table: T,
  onConflict: string,
  invalidates: QueryKey[] = [[table]],
) {
  const invalidate = useInvalidate(invalidates);
  return useMutation({
    mutationFn: (body: Insert<T>) => api.upsert(table, onConflict, body),
    onSuccess: invalidate,
  });
}

export function useRemove<T extends Table>(table: T, invalidates: QueryKey[] = [[table]]) {
  const invalidate = useInvalidate(invalidates);
  return useMutation({
    mutationFn: (id: number) => api.remove(table, id),
    onSuccess: invalidate,
  });
}

export type { Row };
